// ============================================================
// Die Startseite (SPEC §32): ihre Einstellungen, die privaten Serien und
// Abzeichen und „Zuletzt im Haus" — drei Routen, vier Haken.
//
// Seit §34 (036) tragen dieselben Einstellungen auch „Meine Links" auf
// `/links`: `eigene` ist die Lesezeichenliste (bis 100, je Eintrag ein
// optionaler Ordner), `ordner` die Reihenfolge der Ordner. Kein zweiter
// Haken und kein zweiter Schlüssel — eine angeheftete Kachel zeigt per
// Kennung auf genau diese Einträge, und der PUT schreibt beides in einem Zug.
//
// Eine eigene Datei neben `hooks.ts` und nicht darin: `hooks.ts` ist die
// meistgeänderte Datei des Frontends, und sechs Leute bauen die Startseite
// gleichzeitig. Die Regeln aus dem Kopf von `hooks.ts` gelten hier genauso
// (Serverdaten gehören TanStack Query; Pfade ohne `/api`).
//
// Die Schlüssel liegen alle unter `["startseite"]`. ACHTUNG: dieser Präfix
// ist ZUGLEICH der Schlüssel der Einstellungen selbst — wer nur sie meint,
// sagt `exact: true`, sonst trifft er die Abzeichen mit.
// ============================================================
import { useEffect } from "react";
import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import { api } from "./client";
import type {
  EigenerLink,
  MeineErfolge,
  SpielStufe,
  StartAufbau,
  Startseite,
  StartseiteSetzen,
  ZuletztEintrag,
} from "./types";

/* ========================================================== Schlüssel */

export const startseiteSchluessel = {
  /** Präfix für alles der Startseite — und der Schlüssel der Einstellungen (siehe Kopf). */
  alle: ["startseite"] as const,
  einstellungen: ["startseite"] as const,
  /** Serien und Abzeichen der fragenden Person (nur Stufe „viel"). */
  erfolge: ["startseite", "erfolge"] as const,
  /** „Zuletzt im Haus". `lib/ereignisse.ts` entwertet ihn bei projekt/abgabe/bild/beitrag. */
  zuletzt: ["startseite", "zuletzt"] as const,
};

/** Der Schlüssel der Speicher-Änderung. Die Wache unten erkennt daran die
 *  eigene Einstellungsänderung und lässt sie aus: eine umsortierte Kachel
 *  verdient kein Abzeichen, also auch keine neue Abzeichen-Abfrage. */
const SETZEN = ["startseite", "setzen"] as const;

/**
 * Das Kennzeichen einer Änderung, die KEINE eigene Handlung ist — ein Aufruf,
 * den die Oberfläche von selbst schickt (die Gelesen-Marke beim Öffnen von
 * Aktuelles). Die Wache unten lässt sie aus: sonst öffnete schon das bloße
 * Lesen das Feierfenster, und ein Abzeichen, das andere verdient haben (eine
 * Reaktion auf „Neu im Haus"), bekäme Konfetti (E32.5, §32.9 Regel 8).
 * Als `meta` an `useMutation` hängen: `meta: KEINE_HANDLUNG`.
 */
export const KEINE_HANDLUNG = { keineHandlung: true } as const;

/* ========================================================== Vorgaben und Grenzen */

/** Angeheftet, solange jemand nie selbst gewählt hat (`kacheln === null`, E32.7).
 *  Kennungen aus dienste.json; „projekte" steht bewusst nicht darin — das wäre
 *  diese Anwendung selbst (`istDieseAnwendung`). */
export const START_VORGABE_KACHELN: readonly string[] = ["zeitplan", "entwaesserung", "bauleitung", "fotos", "webgis", "beteiligung"];

/** Die Spielstufe ohne eigene Wahl. Dieselbe Vorgabe steht in Wanderung 034 und
 *  als `SPIEL_VORGABE` im Router — wer sie ändert, ändert alle drei. */
export const SPIEL_VORGABE: SpielStufe = "etwas";

export const AUFBAU_VORGABE: StartAufbau = "voll";

/** Was `GET /me/startseite` ohne gespeicherte Zeile antwortet. Nie verändern —
 *  immer kopieren (`{ ...STARTSEITE_VORGABE }`). */
export const STARTSEITE_VORGABE: Startseite = {
  kacheln: null,
  eigene: [],
  ordner: [],
  aufbau: AUFBAU_VORGABE,
  spiel: SPIEL_VORGABE,
};

/** Dieselben Grenzen wie Router und CHECKs (034, 036) — die Oberfläche prüft vorher,
 *  damit ein Feld den Satz zeigt und nicht erst der 422. `test_startseite.py` vergleicht
 *  jede dieser Zahlen mit der gleichnamigen im Router.
 *  `EIGENE_MAX` war bis §34 zwölf (nur Kacheln); seit die eigenen Links „Meine Links"
 *  sind, gehen hundert — angeheftet bleiben höchstens `KACHELN_MAX`. */
export const KACHELN_MAX = 40;
export const EIGENE_MAX = 100;
export const EIGEN_NAME_MAX = 40;
export const EIGEN_URL_MAX = 300;
/** Ordner unter „Meine Links" (§34, 036): höchstens 20, Namen höchstens 40 Zeichen. */
export const ORDNER_MAX = 20;
export const ORDNER_NAME_MAX = 40;
/** Eine Dienstkennung aus dienste.json (`zeitplan`, `bt-rotenburg`, `dev-webgis` …). */
export const KACHEL_MUSTER = /^[a-z0-9][a-z0-9-]{0,39}$/;
/** Die Kennung eines eigenen Links. Sie passt zugleich auf `KACHEL_MUSTER`,
 *  darum darf sie in `kacheln` stehen. */
export const EIGEN_MUSTER = /^eigen-[0-9a-f]{8}$/;

/** Die angehefteten Kennungen in ihrer Reihenfolge — die Vorgabe, solange nie gewählt wurde. */
export function kachelnVon(s: Startseite): string[] {
  return s.kacheln ?? [...START_VORGABE_KACHELN];
}

/** Eine neue Kennung für einen eigenen Link, die es in `vorhanden` noch nicht gibt. */
export function neueEigeneId(vorhanden: readonly EigenerLink[] = []): string {
  const belegt = new Set(vorhanden.map((e) => e.id));
  for (;;) {
    const bytes = new Uint8Array(4);
    crypto.getRandomValues(bytes);
    const id = `eigen-${Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("")}`;
    if (!belegt.has(id)) return id;
  }
}

/** Ergebnis von `eigeneAdresseLesen`. */
export type EigeneAdresse = { ok: true; wert: string } | { ok: false; grund: "leer" | "https" | "lang" };

/**
 * Die Adresse eines eigenen Links prüfen — mit der Regel des Routers: beginnt
 * mit `https://` und einem Rechnernamen, kein Leerraum, höchstens 300 Zeichen.
 * Gekürzt wird nie; `http://` wird nicht still zu `https://` gemacht (ob die
 * Gegenseite das kann, wissen wir nicht).
 */
export function eigeneAdresseLesen(eingabe: string): EigeneAdresse {
  const wert = eingabe.trim();
  if (wert === "") return { ok: false, grund: "leer" };
  if (wert.length > EIGEN_URL_MAX) return { ok: false, grund: "lang" };
  if (!/^https:\/\/[^\s/]/.test(wert) || /\s/.test(wert)) return { ok: false, grund: "https" };
  return { ok: true, wert };
}

/** Ergebnis von `ordnerNameLesen`. */
export type OrdnerName = { ok: true; wert: string } | { ok: false; grund: "leer" | "doppelt" | "voll" };

/**
 * Einen neuen Ordnernamen prüfen (§34.3, Regel 1) — bevor er gespeichert wird.
 *
 * Leerraum zusammengezogen und auf `ORDNER_NAME_MAX` gekürzt wie im Router (ein
 * Anzeigename, der zwei Zeichen zu lang ist, ist kein Fehler). „Doppelt" ist hier
 * STRENGER als der Server: der vergleicht wörtlich, hier zählen Groß/klein und
 * Akzente nicht — sonst entstünden „Normen" und „normen" als zwei Ordner, und
 * niemand wüsste, in welchem was liegt. Wer umbenennt, gibt den alten Namen nicht
 * in `vorhanden` mit (sonst wäre „normen" → „Normen" doppelt mit sich selbst).
 */
export function ordnerNameLesen(eingabe: string, vorhanden: readonly string[]): OrdnerName {
  const wert = eingabe.replace(/\s+/g, " ").trim().slice(0, ORDNER_NAME_MAX).trim();
  if (wert === "") return { ok: false, grund: "leer" };
  if (vorhanden.some((o) => o.localeCompare(wert, undefined, { sensitivity: "base" }) === 0)) return { ok: false, grund: "doppelt" };
  if (vorhanden.length >= ORDNER_MAX) return { ok: false, grund: "voll" };
  return { ok: true, wert };
}

/* ========================================================== Einstellungen */

const holeStartseite = () => api.get<Startseite>("/me/startseite");

/** Was `useStartseite` gibt: den Wert (nie `undefined`), ob er feststeht, und die Abfrage selbst. */
export interface StartseiteStand {
  /** Die gespeicherten Einstellungen — bis sie da sind (oder wenn die Abfrage scheitert) die Vorgaben. */
  wert: Startseite;
  /** `true`, sobald die Abfrage entschieden ist (Erfolg ODER Fehler). Die Startseite wartet darauf,
   *  bevor sie einen Aufbau wählt — sonst blitzte das Cockpit auf, bevor „Nur Kacheln" ankommt. */
  bereit: boolean;
  /** `true`, wenn das Lesen gescheitert ist und KEIN gespeicherter Stand vorliegt. Dann ist
   *  `wert` nur die Vorgabe — wer daraus Kacheln oder eigene Links baut und speichert, ersetzt
   *  damit still die gespeicherten (der PUT ersetzt die ganze Zeile). Also: nichts bearbeiten
   *  lassen, sondern den Fehler sagen. */
  fehler: boolean;
  abfrage: UseQueryResult<Startseite>;
}

/**
 * `GET /me/startseite` — die eigenen Einstellungen (E32.3).
 *
 * Beim Fensterwechsel neu geholt, anders als die Vorgabe im QueryClient: die
 * Tabelle hat keinen NOTIFY-Auslöser (Wanderung 034), also ist der Fokus der
 * Moment, in dem ein zweites Gerät erfährt, was auf dem ersten umgestellt wurde.
 */
export function useStartseite(aktiv = true): StartseiteStand {
  const abfrage = useQuery({
    queryKey: startseiteSchluessel.einstellungen,
    queryFn: holeStartseite,
    enabled: aktiv,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });
  return {
    wert: abfrage.data ?? STARTSEITE_VORGABE,
    bereit: abfrage.isSuccess || abfrage.isError,
    fehler: abfrage.isError && abfrage.data === undefined,
    abfrage,
  };
}

/** Was eine Änderung mitschickt: nur die Felder, die sich ändern. */
export type StartseiteTeil = Partial<StartseiteSetzen>;

interface SetzenKontext {
  vorher: Startseite;
}

/** Ein Teil über einen Stand gelegt. Feld für Feld und nicht mit `...teil`: ein
 *  ausdrücklich mitgeschicktes `undefined` darf keinen gespeicherten Wert löschen
 *  (`kacheln: null` dagegen ist ein Wert — „Vorgabe"). */
function zusammen(stand: Startseite, teil: StartseiteTeil): Startseite {
  return {
    kacheln: teil.kacheln !== undefined ? teil.kacheln : stand.kacheln,
    eigene: teil.eigene ?? stand.eigene,
    // `?? []` für einen Stand aus der Zeit vor §34, der noch im Zwischenspeicher
    // liegen kann (ein offenes Fenster über das Ausrollen hinweg): ohne `ordner`
    // wäre der PUT ein 422 (E34.8).
    ordner: teil.ordner ?? stand.ordner ?? [],
    aufbau: teil.aufbau ?? stand.aufbau,
    spiel: teil.spiel ?? stand.spiel,
  };
}

/**
 * `PUT /me/startseite` — optimistisch, mit Rücknahme bei Fehler.
 *
 * Aufgerufen wird mit einem TEIL (`mutate({ kacheln })`, `mutate({ spiel })`);
 * geschickt wird immer der GANZE Stand, weil der PUT ersetzt. Der ganze Stand
 * wird erst beim Absenden aus dem Zwischenspeicher gebaut und nicht beim
 * Aufrufer: sonst schickte ein entprelltes Kachel-Speichern (600 ms) den
 * Aufbau von VOR dem Umschalten mit und stellte ihn still zurück.
 *
 * `scope` reiht alle Änderungen hintereinander: zwei schnelle Züge kommen in
 * der Reihenfolge an, in der sie gemacht wurden, und die Antwort des ersten
 * überschreibt nie die Anzeige des zweiten.
 *
 * Kein Toast hier — der Aufrufer weiß, was schiefging (`mutate(teil, { onError })`).
 * Entwertet wird sonst nichts; die eine Ausnahme ist die Spielstufe (unten).
 */
export function useStartseiteSetzen(): UseMutationResult<Startseite, unknown, StartseiteTeil, SetzenKontext> {
  const qc = useQueryClient();
  const schluessel = startseiteSchluessel.einstellungen;
  return useMutation<Startseite, unknown, StartseiteTeil, SetzenKontext>({
    mutationKey: SETZEN,
    scope: { id: "startseite" },
    onMutate: async (teil) => {
      // Nie auf die Vorgabe schreiben, solange der echte Stand nicht gelesen ist:
      // der PUT ersetzt ALLES und löschte sonst die gespeicherten Kacheln.
      // Scheitert das Lesen, scheitert die Änderung — nichts wird geschickt.
      const gelesen = qc.getQueryData<Startseite>(schluessel);
      const vorher: Startseite = gelesen ?? (await qc.fetchQuery({ queryKey: schluessel, queryFn: holeStartseite }));
      await qc.cancelQueries({ queryKey: schluessel, exact: true });
      // Nach dem Warten NEU lesen: kam inzwischen eine zweite Änderung dazu, legt
      // sich diese über sie, statt sie mit dem Stand von vorhin zu überschreiben.
      qc.setQueryData<Startseite>(schluessel, zusammen(qc.getQueryData<Startseite>(schluessel) ?? vorher, teil));
      return { vorher };
    },
    mutationFn: (teil) => {
      const stand = qc.getQueryData<Startseite>(schluessel);
      if (!stand) return Promise.reject(new Error("Die Startseite ist noch nicht geladen."));
      // Der Zwischenspeicher trägt nach `onMutate` schon diesen Teil (und jeden
      // späteren, der inzwischen dazukam) — `zusammen` ist hier nur die Absicherung.
      const rumpf: StartseiteSetzen = zusammen(stand, teil);
      return api.put<Startseite>("/me/startseite", rumpf);
    },
    onError: (_fehler, _teil, ctx) => {
      // Nur die LETZTE wartende Änderung darf zurückrollen — eine frühere würde die
      // Anzeige einer späteren überschreiben. Danach ehrlich neu lesen: nach einem
      // Fehler weiß nur der Server, was gilt.
      if (qc.isMutating({ mutationKey: SETZEN }) <= 1 && ctx) qc.setQueryData(schluessel, ctx.vorher);
      void qc.invalidateQueries({ queryKey: schluessel, exact: true });
    },
    onSuccess: (antwort, _teil, ctx) => {
      if (qc.isMutating({ mutationKey: SETZEN }) <= 1) qc.setQueryData(schluessel, antwort);
      // Die eine Ausnahme von „entwertet sonst nichts": die Abzeichen-Antwort hängt an
      // der Stufe. Wer auf „viel" schaltet, soll nicht die leere Antwort sehen, die der
      // Server eine Sekunde vorher unter „etwas" gegeben hätte.
      if (ctx && ctx.vorher.spiel !== antwort.spiel) {
        void qc.invalidateQueries({ queryKey: startseiteSchluessel.erfolge });
      }
    },
  });
}

/* ========================================================== Serien und Abzeichen */

/** Wie lange nach einer eigenen Änderung ein neues Abzeichen als ihre FOLGE gilt
 *  (und gefeiert wird). Alles, was später oder ohne eigene Handlung auftaucht,
 *  bekommt nur den stillen „neu"-Merker (E32.5: nie beim bloßen Lesen feiern). */
export const ERFOLGE_FEIER_FENSTER = 15_000;

let letzteAenderung = 0;
const bewacht = new WeakSet<QueryClient>();

/**
 * Die Wache: jede EIGENE erfolgreiche Änderung (eine Reaktion, eine Abgabe
 * abgehakt, ein Bild, ein Beitrag, der Steckbrief …) entwertet die Abzeichen.
 * Ausgenommen: die eigene Einstellungsänderung (`SETZEN`) und alles, was
 * `KEINE_HANDLUNG` trägt — Aufrufe, die ohne Zutun der Person laufen.
 *
 * Warum so und nicht über den Live-Strom: die Meldungen tragen keine Person
 * (`Ereignis` hat keine `user_id`). Entwertete jede Meldung die Abzeichen,
 * rechnete jede offene Startseite des Hauses ihre Abzeichen-Abfrage neu, sobald
 * irgendwer irgendwo klatscht. Und warum nicht in jedem Änderungshaken von
 * `hooks.ts` einzeln: das wären ein Dutzend Stellen in einer Datei, die allen
 * gehört — und die nächste neue Änderung vergäße es.
 *
 * Einmal je QueryClient angemeldet, nie abgemeldet: der Client lebt so lange
 * wie die Seite. `invalidateQueries` holt nur neu, was gerade angezeigt wird.
 */
function wacheAnmelden(qc: QueryClient): void {
  if (bewacht.has(qc)) return;
  bewacht.add(qc);
  qc.getMutationCache().subscribe((e) => {
    if (e.type !== "updated" || e.action.type !== "success") return;
    const k = e.mutation.options.mutationKey;
    if (k && k[0] === SETZEN[0] && k[1] === SETZEN[1]) return;
    if (e.mutation.options.meta?.keineHandlung === true) return;
    letzteAenderung = Date.now();
    void qc.invalidateQueries({ queryKey: startseiteSchluessel.erfolge });
  });
}

/** Beim Abmelden: die letzte eigene Änderung gehörte der vorigen Person. Meldet sich
 *  im selben Fenster jemand anders an, darf deren erste Abfrage nicht in ihr Feierfenster fallen. */
export function eigeneAenderungVergessen(): void {
  letzteAenderung = 0;
}

/** Zeitpunkt (ms) der letzten eigenen erfolgreichen Änderung in diesem Fenster; 0 = keine. */
export function letzteEigeneAenderung(): number {
  return letzteAenderung;
}

/** Ob eine Antwort vom Zeitpunkt `zeitpunkt` (z. B. `dataUpdatedAt`) die Folge einer eigenen
 *  Änderung ist — nur dann darf ein neues Abzeichen gefeiert werden. */
export function folgtEigenerAenderung(zeitpunkt: number): boolean {
  return letzteAenderung > 0 && zeitpunkt >= letzteAenderung && zeitpunkt - letzteAenderung <= ERFOLGE_FEIER_FENSTER;
}

/**
 * `GET /me/erfolge` — Serien und Abzeichen, NUR die eigenen (E32.5).
 *
 * `aktiv` ist `spiel === "viel"`: auf den anderen Stufen wird gar nicht
 * gefragt (und der Server rechnete auch nichts). Eine Minute frisch — die
 * Wache oben holt nach jeder eigenen Änderung sofort nach, also muss die
 * Zeit nicht kürzer sein.
 */
export function useMeineErfolge(aktiv: boolean): UseQueryResult<MeineErfolge> {
  const qc = useQueryClient();
  useEffect(() => {
    if (aktiv) wacheAnmelden(qc);
  }, [qc, aktiv]);
  return useQuery({
    queryKey: startseiteSchluessel.erfolge,
    queryFn: () => api.get<MeineErfolge>("/me/erfolge"),
    enabled: aktiv,
    staleTime: 60_000,
  });
}

/* ========================================================== Zuletzt im Haus */

/** `GET /start/zuletzt` — je Projekt das jüngste Ereignis der letzten 60 Tage, höchstens zwölf (E32.8). */
export function useZuletzt(aktiv = true): UseQueryResult<ZuletztEintrag[]> {
  return useQuery({
    queryKey: startseiteSchluessel.zuletzt,
    queryFn: () => api.get<ZuletztEintrag[]>("/start/zuletzt"),
    enabled: aktiv,
    staleTime: 60_000,
  });
}
